import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import Admin from "@/pages/Admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Lock } from "lucide-react";

// The /admin route is a "secret" admin login: unauthenticated (or non-admin) visitors see the
// login form right here instead of being bounced to the public /login. Admins see the dashboard.
export default function AdminGate() {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="admin-gate-loading">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  if (user && user.role === "admin") return <Admin />;
  return <AdminLogin />;
}

function AdminLogin() {
  const { login, logout, apiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        await logout();
        setError("This account doesn't have admin access.");
      }
      // On an admin login, AuthContext updates `user` and AdminGate re-renders into the dashboard.
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background mm-grid-bg flex flex-col items-center justify-center px-4 py-10" data-testid="admin-login-page">
      <div className="w-full max-w-sm mm-fade-in">
        <div className="flex flex-col items-center mb-6">
          <div className="h-14 w-14 rounded-2xl bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mb-4">
            <ShieldCheck className="h-7 w-7 text-rose-400" />
          </div>
          <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-rose-400">Restricted</span>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/70 backdrop-blur-xl p-7 shadow-2xl">
          <h1 className="font-display text-2xl font-bold text-center">Admin Access</h1>
          <p className="mt-2 text-sm text-slate-400 text-center">Authorized personnel only.</p>
          <form onSubmit={submit} className="space-y-4 mt-6">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="admin-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-slate-950 border-slate-700 h-11" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-slate-950 border-slate-700 h-11" />
            </div>
            {error && <p data-testid="admin-login-error" className="text-sm text-rose-400">{error}</p>}
            <Button data-testid="admin-login-submit" type="submit" disabled={loading} className="w-full h-11 bg-rose-500 hover:bg-rose-400 text-slate-950 font-semibold">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Lock className="h-4 w-4 mr-2" /> Enter</>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
